// Pricing page (/pricing, /en/pricing), shared by PricingPage.jsx and the
// search-engine version in api/features-og.js. Plain data — no JSX.
// Keep the plan matrix true to App.jsx PLANS / PLAN_COMPARE_ROWS and the
// trial to start_free_trial (supabase/migrations/20261022_pro_free_trial.sql).

export const PLAN_KEYS = ["free", "basic", "pro", "enterprise"];
export const PLAN_PRICES = { free: 0, basic: 128, pro: 268, enterprise: 528 };   // HKD / month
export const ANNUAL_MONTHS = 10;                                                  // yearly = 10 × monthly
export const TRIAL_DAYS = 14;
const USD = (hkd) => Math.round(hkd / 7.8);   // HKD is pegged ~7.8 to the USD

// Cell values: true = included, false = not included, string = shown as is.
const Y = true, N = false;
const ROWS = [
  // [zh, en, free, basic, pro, enterprise]
  ["員工 / 美容師", "Staff / groomers", "1", "3", "5", "8"],
  ["預約排程（日 / 週 / 月日曆、即場到店）", "Appointment calendar (day / week / month, walk-ins)", Y, Y, Y, Y],
  ["顧客同毛孩資料", "Customer & pet records", Y, Y, Y, Y],
  ["價目表 + QR 海報", "Price list + QR poster", Y, Y, Y, Y],
  ["店舖專頁 + 美容店目錄", "Shop page + groomer directory listing", Y, Y, Y, Y],
  ["客人網上自助預約", "Online self-booking for customers", ["每月 10 個", "10 / month"], ["無限", "Unlimited"], ["無限", "Unlimited"], ["無限", "Unlimited"]],
  ["排班系統", "Staff roster", N, Y, Y, Y],
  ["電郵確認同提醒", "Email confirmations & reminders", N, Y, Y, Y],
  ["營業報表", "Business reports", N, Y, Y, Y],
  ["收銀 POS（收款、收據、商品）", "POS (checkout, receipts, products)", N, ["100 件商品", "100 products"], ["500 件商品", "500 products"], ["無上限", "Unlimited"]],
  ["收銀：庫存、退款換貨、交更、優惠", "POS: stock, refunds & exchanges, shifts, promotions", N, N, Y, Y],
  ["收銀：採購、AI 新增商品、批量匯入、毛利報表", "POS: purchasing, AI product setup, import, margin reports", N, N, N, Y],
  ["WhatsApp 預約確認（你自己嘅號碼）", "WhatsApp booking confirmations (your own number)", N, N, Y, Y],
  ["會員錢包、套票", "Customer wallet & packages", N, N, Y, Y],
  ["電子印花卡", "Digital stamp card", N, N, Y, Y],
  ["店主 AI 助手", "AI assistant for owners", N, N, Y, Y],
  ["會員積分", "Loyalty points", N, N, N, Y],
  ["品牌專屬 App、移除 PettoBook 標誌", "Your own branded app, no PettoBook branding", N, N, N, Y],
];

const ZH_FAQ = [
  ["試用要唔要信用卡？", `唔使。每間新店舖開店後自動有 ${TRIAL_DAYS} 日專業版免費試用，試用期間所有專業版功能都用得。試用完如果冇升級，會自動轉做免費版，唔會收任何費用。`],
  ["試用完會點？我啲資料會唔會唔見？", "唔會。所有預約、客人、毛孩同紀錄都會保留。轉返免費版之後：收銀 POS 會暫停、網上預約變返每月 10 個、超出名額嘅員工會暫停接新預約。隨時升級就即刻全部恢復。"],
  ["可以月付定年付？", `都得。年付 = 月費 × ${ANNUAL_MONTHS}，即係送 2 個月。可以隨時轉方案或取消，冇長約。`],
  ["點樣付款？", "網頁版用信用卡（Stripe）付款；Android App 用 Google Play 訂閱。收費以港幣計算。"],
  ["升級或者降級幾時生效？", "升級即時生效，只收今期差價。取消或降級會用到今期完結先轉。"],
  ["我喺香港以外開舖，可以用嗎？", "可以。PettoBook 支援唔同國家同貨幣（例如台灣、新加坡、馬來西亞、美國、加拿大、日本），預約、收款、收據都會用你嘅貨幣顯示。訂閱費以港幣收取。"],
  ["WhatsApp AI 自動回覆包唔包？", "唔包。AI 自動回覆要另外開通第三方 Chatwoot 或 WATI 帳戶（另外收費），再接駁 PettoBook。"],
];
const EN_FAQ = [
  ["Do I need a credit card for the trial?", `No. Every new shop gets a ${TRIAL_DAYS}-day free trial of Pro automatically, with every Pro feature switched on. If you don't upgrade, you move to the Free plan when it ends — nothing is charged.`],
  ["What happens when the trial ends? Do I lose my data?", "No. Every booking, customer, pet and record is kept. On Free, the POS pauses, online booking goes back to 10 a month, and staff over the plan's limit can't take new bookings. Upgrade any time and everything comes back instantly."],
  ["Monthly or yearly?", `Either. Yearly costs ${ANNUAL_MONTHS}× the monthly price — two months free. Change plan or cancel any time; there's no long contract.`],
  ["How do I pay?", "By card (Stripe) on the web, or through a Google Play subscription in the Android app. Subscriptions are billed in Hong Kong dollars (HKD); the US-dollar amounts shown are approximate."],
  ["When do upgrades and downgrades take effect?", "Upgrades apply immediately and you only pay the difference for the current period. Cancellations and downgrades take effect at the end of the period."],
  ["My salon isn't in Hong Kong. Can I use PettoBook?", "Yes. PettoBook supports shops in other countries and currencies (for example Taiwan, Singapore, Malaysia, the US, Canada and Japan): bookings, payments and receipts show your own currency. The subscription itself is billed in HKD."],
  ["Is WhatsApp AI auto-reply included?", "No. AI auto-reply needs a separately paid third-party Chatwoot or WATI account, which you then connect to PettoBook."],
];

export const PRICING = {
  zh: {
    lang: "zh-HK", path: "/pricing", other: ["/en/pricing", "English"],
    seo: {
      title: "寵物美容店系統收費｜免費版、基本版 HK$128、專業版、旗艦版 · 14 日免費試用 · PettoBook",
      description: `PettoBook 寵物美容店預約系統收費：免費版永久免費；基本版 HK$128/月、專業版 HK$268/月、旗艦版 HK$528/月，年付送 2 個月。新店舖送 ${TRIAL_DAYS} 日專業版免費試用，唔使信用卡。`,
      keywords: "寵物美容系統收費,寵物美容預約系統價錢,美容店系統月費,寵物店 POS 收費,寵物美容軟件,PettoBook 收費",
      crumb: "收費方案", home: "主頁",
    },
    title: "簡單收費，冇長約",
    lead: `免費開店，需要更多先升級。每間新店舖送 ${TRIAL_DAYS} 日專業版免費試用，唔使信用卡。`,
    trial: `🎁 新店舖：${TRIAL_DAYS} 日專業版免費試用 · 唔使信用卡 · 試用完自動轉免費版，唔會收費`,
    monthly: "月付", yearly: "年付", yearlySave: "送 2 個月",
    perMonth: "/月", perYear: "/年", billedYearly: (y) => `年付 HK$${y}`,
    names: { free: "免費版", basic: "基本版", pro: "專業版", enterprise: "旗艦版" },
    taglines: { free: "開店、上目錄、試用網上預約", basic: "網上預約 + 收銀", pro: "最受歡迎：會員、WhatsApp、庫存", enterprise: "會員積分、品牌專屬 App" },
    popular: "最受歡迎",
    cta: "免費開店 · 試用 14 日專業版", ctaFree: "免費開店",
    compareTitle: "功能比較", feature: "功能",
    faqTitle: "收費常見問題",
    note: "* 收費以港幣（HKD）計算。WhatsApp AI 自動回覆需另外開通 Chatwoot 或 WATI。",
    links: [["/features", "功能介紹"], ["/help", "使用教學"], ["/en/pricing", "English"]],
    faq: ZH_FAQ,
    price: (k) => PLAN_PRICES[k] ? `HK$${PLAN_PRICES[k]}` : "免費",
    rows: ROWS.map((r) => [r[0], ...r.slice(2).map((v) => Array.isArray(v) ? v[0] : v)]),
  },
  en: {
    lang: "en", path: "/en/pricing", other: ["/pricing", "中文"],
    seo: {
      title: "Pet Grooming Software Pricing | Free Plan, 14-Day Free Pro Trial · PettoBook",
      description: `PettoBook pricing for grooming salons: a free plan forever, Basic HK$128 (≈ US$${USD(128)}) a month, Pro HK$268 (≈ US$${USD(268)}), Enterprise HK$528 (≈ US$${USD(528)}). Two months free on yearly billing. ${TRIAL_DAYS}-day free Pro trial, no credit card.`,
      keywords: "pet grooming software pricing,dog grooming software cost,grooming salon booking software price,pet salon POS pricing,grooming business software free trial",
      crumb: "Pricing", home: "Home",
    },
    title: "Simple pricing, no contract",
    lead: `Start free and upgrade when you need more. Every new salon gets a ${TRIAL_DAYS}-day free Pro trial — no credit card.`,
    trial: `🎁 New salons: ${TRIAL_DAYS}-day free Pro trial · no credit card · moves to Free afterwards, nothing charged`,
    monthly: "Monthly", yearly: "Yearly", yearlySave: "2 months free",
    perMonth: "/mo", perYear: "/yr", billedYearly: (y) => `HK$${y} billed yearly`,
    names: { free: "Free", basic: "Basic", pro: "Pro", enterprise: "Enterprise" },
    taglines: { free: "Your shop page, directory listing, try online booking", basic: "Online booking + POS", pro: "Most popular: memberships, WhatsApp, stock", enterprise: "Loyalty points, your own branded app" },
    popular: "Most popular",
    cta: "Start free · 14-day Pro trial", ctaFree: "Start free",
    compareTitle: "Compare plans", feature: "Feature",
    faqTitle: "Pricing FAQ",
    note: "* Billed in Hong Kong dollars (HKD); US-dollar amounts are approximate. WhatsApp AI auto-reply needs a separate Chatwoot or WATI account.",
    links: [["/en/features", "Features"], ["/en/help", "Help centre"], ["/pricing", "中文"]],
    faq: EN_FAQ,
    price: (k) => PLAN_PRICES[k] ? `HK$${PLAN_PRICES[k]}` : "Free",
    usd: (k) => PLAN_PRICES[k] ? `≈ US$${USD(PLAN_PRICES[k])}` : "",
    rows: ROWS.map((r) => [r[1], ...r.slice(2).map((v) => Array.isArray(v) ? v[1] : v)]),
  },
};
